import { useEffect, useRef, useState } from 'react';
import { Alert, Loader, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

type Props = { url: string };
export function QuotePdfPreview({ url }: Props) {
  const { t } = useTranslation();
  const container = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  useEffect(() => {
    let stopped = false;
    let destroy: (() => void) | undefined;
    setLoading(true); setError(false);
    const render = async () => {
      try {
        const { getDocument, GlobalWorkerOptions } = await import('pdfjs-dist');
        GlobalWorkerOptions.workerSrc = workerUrl;
        const task = getDocument({ url });
        destroy = () => { void task.destroy(); };
        const document = await task.promise;
        if (stopped || !container.current) return;
        container.current.replaceChildren();
        const width = Math.max(200, Math.min(container.current.clientWidth - 24, 900));
        const density = Math.min(window.devicePixelRatio || 1, 2);
        for (let number = 1; number <= document.numPages; number++) {
          if (stopped || !container.current) break;
          const page = await document.getPage(number);
          const viewport = page.getViewport({ scale: width / page.getViewport({ scale: 1 }).width * density });
          const canvas = window.document.createElement('canvas');
          canvas.width = Math.ceil(viewport.width); canvas.height = Math.ceil(viewport.height);
          canvas.style.cssText = `display:block;width:${width}px;max-width:100%;height:auto;margin:0 auto 16px;background:white;box-shadow:0 1px 6px #0002`;
          canvas.setAttribute('role', 'img');
          canvas.setAttribute('aria-label', `${t('opportunity.quote.preview')} - ${number}`);
          container.current.append(canvas);
          await page.render({ canvas, viewport }).promise;
        }
      } catch { if (!stopped) setError(true); }
      finally { if (!stopped) setLoading(false); }
    };
    void render();
    return () => { stopped = true; destroy?.(); };
  }, [url, t]);
  return <div style={{ height: '70vh', overflowY: 'auto', background: '#edf0ef', borderRadius: 6, padding: 12 }}>
    {loading && <Text align="center" py="md"><Loader size="sm"/></Text>}
    {error && <Alert color="red">{t('common.attachments.previewFailed')}</Alert>}
    <div ref={container}/>
  </div>;
}
